/**
 * Product Availability Module
 *
 * Uses the stock cache to show real-time availability on product pages
 * and listing pages (collection/index cards).
 *
 * On product pages, stock status is integrated into the delivery row:
 * - In stock:    show shipping + delivery time normally
 * - Low stock:   show shipping + "Nur noch X auf Lager" in amber
 * - Pre-order:   show shipping + "Vorbestellen" in amber
 * - Sold out:    show "Nicht auf Lager" in red, hide shipping/delivery
 */

import { getStock } from "./stock-cache.js";

const LOW_STOCK_THRESHOLD = 5;

export async function init() {
  const stock = await getStock();
  if (!stock) {
    return;
  }

  const lang = (window.VANYX_CONFIG || {}).lang || "de";

  // Update product detail page
  updateProductPage(stock, lang);

  // Update listing cards (collection & shop index)
  updateListingCards(stock, lang);
}

function updateProductPage(stock, lang) {
  const product = document.querySelector("[data-shopify-handle]");
  if (!product) {
    return;
  }

  const handle = product.dataset.shopifyHandle;
  const isPreorder = product.hasAttribute("data-preorder");
  const variants = stock[handle];
  if (!variants) {
    return;
  }

  // Update variant buttons
  const buttons = document.querySelectorAll("[data-variant-btn]");
  buttons.forEach((btn) => {
    const gid = `gid://shopify/ProductVariant/${btn.dataset.variantId}`;
    const variant = variants.find((v) => v.id === gid);
    if (!variant) {
      return;
    }

    if (!variant.available) {
      btn.disabled = true;
      btn.dataset.available = "false";
      btn.classList.add("shop-product__variant-btn--unavailable");
    } else {
      btn.disabled = false;
      btn.dataset.available = "true";
      btn.classList.remove("shop-product__variant-btn--unavailable");
    }
  });

  // Update delivery row with stock status
  const deliveryRow = document.querySelector("[data-delivery-row]");
  if (!deliveryRow) {
    return;
  }

  const activeBtn = document.querySelector("[data-variant-btn].shop-product__variant-btn--active");
  const activeGid = activeBtn
    ? `gid://shopify/ProductVariant/${activeBtn.dataset.variantId}`
    : null;
  const activeVariant = activeGid
    ? variants.find((v) => v.id === activeGid)
    : null;
  const fallback = variants.find((v) => v.available) || variants[0];
  const currentVariant = activeVariant || fallback;

  updateDeliveryRow(deliveryRow, currentVariant, isPreorder, lang);

  // Listen for variant changes
  const variantsContainer = document.querySelector("[data-variants]");
  if (variantsContainer) {
    variantsContainer.addEventListener("variantchange", (e) => {
      const gid = `gid://shopify/ProductVariant/${e.detail.variantId}`;
      const v = variants.find((vr) => vr.id === gid);
      updateDeliveryRow(deliveryRow, v, isPreorder, lang);
    });
  }
}

function updateDeliveryRow(row, variant, isPreorder, lang) {
  const shippingEl = row.querySelector("[data-shipping-cost]");
  const deliveryEl = row.querySelector("[data-delivery-time]");
  const statusEl = row.querySelector("[data-stock-status]");

  // Reset
  row.classList.remove(
    "shop-product__delivery-row--out-of-stock",
    "shop-product__delivery-row--low-stock",
    "shop-product__delivery-row--pre-order",
  );
  if (statusEl) {
    statusEl.textContent = "";
  }

  if (!variant || !variant.available) {
    // Sold out — hide shipping/delivery, show status
    if (shippingEl) {
      shippingEl.hidden = true;
    }
    if (deliveryEl) {
      deliveryEl.hidden = true;
    }
    if (statusEl) {
      statusEl.textContent = lang === "en" ? "Out of stock" : "Nicht auf Lager";
      statusEl.hidden = false;
    }
    row.classList.add("shop-product__delivery-row--out-of-stock");
    return;
  }

  // Available — show shipping/delivery
  if (shippingEl) {
    shippingEl.hidden = false;
  }
  if (deliveryEl) {
    deliveryEl.hidden = false;
  }

  if (isPreorder) {
    if (statusEl) {
      statusEl.textContent = lang === "en" ? "Pre-order" : "Vorbestellen";
      statusEl.hidden = false;
    }
    row.classList.add("shop-product__delivery-row--pre-order");
    return;
  }

  const qty = variant.quantity;
  if (qty !== null && qty !== undefined && qty > 0 && qty <= LOW_STOCK_THRESHOLD) {
    if (statusEl) {
      statusEl.textContent = lang === "en"
        ? `Only ${qty} left`
        : `Nur noch ${qty} auf Lager`;
      statusEl.hidden = false;
    }
    row.classList.add("shop-product__delivery-row--low-stock");
    return;
  }

  // Normal in-stock — no extra status needed
  if (statusEl) {
    statusEl.hidden = true;
  }
}

function updateListingCards(stock, lang) {
  const cards = document.querySelectorAll("[data-product-handle]");
  if (!cards.length) {
    return;
  }

  cards.forEach((card) => {
    const handle = card.dataset.productHandle;
    const isPreorder = card.hasAttribute("data-preorder");
    const variants = stock[handle];
    if (!variants) {
      return;
    }

    const anyAvailable = variants.some((v) => v.available);
    const badge = card.querySelector("[data-stock-badge]");
    if (!badge) {
      return;
    }

    badge.className = "shop-card__stock";
    if (!anyAvailable) {
      badge.textContent = lang === "en" ? "Sold out" : "Ausverkauft";
      badge.classList.add("shop-card__stock--sold-out");
    } else if (isPreorder) {
      badge.textContent = lang === "en" ? "Pre-order" : "Vorbestellen";
      badge.classList.add("shop-card__stock--preorder");
    } else {
      badge.textContent = lang === "en" ? "In stock" : "Auf Lager";
      badge.classList.add("shop-card__stock--in-stock");
    }
    badge.hidden = false;
  });
}
